<template>
  <v-container class="pa-0">
    <!-- Premium Header -->
    <v-sheet color="primary" class="pa-6 d-flex align-center">
      <v-avatar size="40" color="white opacity-20" class="mr-4">
        <v-icon color="white" small>fas fa-clipboard-check</v-icon>
      </v-avatar>
      <div>
        <div class="text-h5 white--text font-weight-black text-start">Reporte de Avance</div>
        <div class="caption white--text opacity-70">Registro masivo de procesos y cumplimiento de metas</div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon color="white" @click="$emit('close')"><v-icon small>fas fa-times</v-icon></v-btn>
    </v-sheet>

    <ValidationObserver v-slot="{ handleSubmit }">
      <v-form @submit.prevent="handleSubmit(save)" class="pa-6">
        <!-- Main Products Table -->
        <v-card outlined class="modern-card overflow-hidden mb-6">
          <v-simple-table class="modern-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center grey lighten-5" width="80">OT</th>
                  <th class="text-left grey lighten-5">Producto / Proceso</th>
                  <th class="text-left grey lighten-5" width="180">Progreso Actual</th>
                  <th class="text-left grey lighten-5" width="180" v-if="machine.production_unit_m2">Producción (m²)</th>
                  <th class="text-left grey lighten-5" width="180" v-else>Producción (Un)</th>
                  <th class="text-center grey lighten-5" width="120" v-if="machine.hour_control">Tiempos</th>
                  <th class="text-center grey lighten-5" width="100">Cerrar?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="process in processes" :key="process.id">
                  <td class="text-center">
                    <v-chip x-small label class="font-weight-black grey lighten-4">#{{ process.work_order }}</v-chip>
                  </td>
                  <td>
                    <div v-if="process.product_sku" class="caption font-weight-black primary--text text-start">
                      [{{ process.product_sku }}]
                    </div>
                    <div class="body-2 font-weight-bold grey--text text--darken-4 text-start">
                      {{ process.product_name }}
                    </div>
                  </td>
                  <!-- Progress Column -->
                  <td>
                    <div v-if="machine.production_unit_m2">
                      <div class="d-flex justify-space-between caption mb-1">
                        <span class="font-weight-bold">{{ process.total_mts2 | number('0.00') }}</span>
                        <span class="grey--text">/ {{ process.product_mts2 * (process.qty_pp === 0 ? 1 : process.qty_pp) | number('0.00') }}</span>
                      </div>
                      <v-progress-linear
                        color="primary"
                        height="4"
                        rounded
                        :value="process.total_mts2 / (process.product_mts2 * (process.qty_pp === 0 ? 1 : process.qty_pp)) * 100"
                      />
                    </div>
                    <div v-else>
                      <div class="d-flex justify-space-between caption mb-1">
                        <span class="font-weight-bold">{{ process.total_qty | number('0.00') }}</span>
                        <span class="grey--text">/ {{ process.product_qty * (process.qty_pp === 0 ? 1 : process.qty_pp) + (process.merma || 0) | number('0.00') }}</span>
                      </div>
                      <v-progress-linear
                        color="primary"
                        height="4"
                        rounded
                        :value="process.total_qty / (process.product_qty * (process.qty_pp === 0 ? 1 : process.qty_pp) + (process.merma || 0)) * 100"
                      />
                    </div>
                  </td>
                  <!-- Input Column -->
                  <td class="pt-4">
                    <ValidationProvider name="Producción" rules="required" v-slot="{ errors }">
                      <v-text-field
                        v-if="machine.production_unit_m2"
                        v-model="process.real_mts2"
                        append-icon="fas fa-ruler-combined"
                        outlined dense hide-details="auto"
                        class="modern-input"
                        type="number"
                        :error-messages="errors"
                        @input="calculateQty(process)"
                      />
                      <v-text-field
                        v-else
                        v-model="process.real_qty"
                        append-icon="fas fa-layer-group"
                        outlined dense hide-details="auto"
                        class="modern-input"
                        type="number"
                        :error-messages="errors"
                        @input="calculateMts2(process)"
                      />
                    </ValidationProvider>
                  </td>
                  <!-- Time Column -->
                  <td v-if="machine.hour_control">
                    <div class="d-flex flex-column gap-1 py-2">
                      <v-text-field v-model="process.start" label="Inicio" type="datetime-local" outlined dense hide-details class="modern-input-mini" />
                      <v-text-field v-model="process.end" label="Fin" type="datetime-local" outlined dense hide-details class="modern-input-mini" />
                    </div>
                  </td>
                  <!-- Close Checkbox -->
                  <td class="text-center">
                    <v-checkbox v-model="process.ask_completed" color="success" hide-details class="mt-0 pt-0 d-inline-block" />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <!-- Summary and Global Fields -->
        <v-row class="mb-6">
          <v-col cols="12" md="4">
            <div class="pa-6 rounded-xl bg-primary-light border-primary-light h-100 text-center d-flex flex-column justify-center align-center">
              <div class="caption primary--text font-weight-black text-uppercase mb-1">Batch Total</div>
              <div class="text-h3 font-weight-black primary--text">
                {{ (machine.production_unit_m2 ? mts2 : qty) | number('0.00') }}
              </div>
              <div class="caption font-weight-bold grey--text text-uppercase">{{ machine.production_unit_m2 ? 'Metros Reales' : 'Unidades Totales' }}</div>
            </div>
          </v-col>
          
          <v-col cols="12" md="8">
            <v-card outlined class="modern-card pa-6 h-100">
              <v-row dense>
                <v-col cols="12">
                  <ValidationProvider name="Máquina" rules="required" v-slot="{ errors }">
                    <v-select
                      label="Equipo Responsable"
                      v-model="equipment"
                      :items="machine.equipments"
                      item-text="name"
                      item-value="id"
                      outlined dense hide-details="auto"
                      class="modern-select mb-4"
                      :error-messages="errors"
                      prepend-inner-icon="fas fa-tools"
                      placeholder="Seleccione la estación o máquina"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="notes"
                    outlined dense hide-details="auto"
                    label="Observaciones del Registro"
                    class="modern-input"
                    placeholder="Detalles sobre el turno, incidencias o material..."
                    rows="3"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <div v-for="group in otGroups" :key="'ot-' + group.work_order" class="mb-6">
          <v-card outlined class="modern-card overflow-hidden">
            <v-sheet color="grey lighten-5" class="px-6 py-3 d-flex align-center">
              <div>
                <div class="caption grey--text font-weight-black text-uppercase">Materiales consumidos</div>
                <div class="subtitle-2 font-weight-black">OT #{{ group.work_order }} · {{ group.product_name }}</div>
              </div>
              <v-spacer />
              <div style="min-width: 320px;">
                <add-article @add="(article) => addMaterial(group.work_order, article)" />
              </div>
            </v-sheet>
            <v-simple-table v-if="(otMaterials[group.work_order] || []).length" class="modern-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="grey lighten-5">Artículo</th>
                    <th class="grey lighten-5" width="160">Cantidad</th>
                    <th class="grey lighten-5" width="120">Unidad</th>
                    <th class="grey lighten-5 text-center" width="110">Reproceso</th>
                    <th class="grey lighten-5" width="56"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, index) in otMaterials[group.work_order]" :key="line.key">
                    <td>
                      <div class="caption font-weight-black primary--text">[{{ line.article.sku }}]</div>
                      <div class="body-2">{{ line.article.name }}</div>
                    </td>
                    <td class="pt-3">
                      <v-text-field
                        v-model.number="line.qty"
                        outlined
                        dense
                        hide-details
                        placeholder="0.00"
                        min="0"
                        step="any"
                        inputmode="decimal"
                        class="qty-input"
                      />
                    </td>
                    <td>
                      <v-chip x-small label outlined class="font-weight-bold">
                        {{ line.unitConsume || 'unidades' }}
                      </v-chip>
                    </td>
                    <td class="text-center">
                      <v-checkbox v-model="line.reprocess" color="error" hide-details class="mt-0 pt-0 d-inline-block" />
                    </td>
                    <td class="text-center">
                      <v-btn icon small @click="removeMaterial(group.work_order, index)">
                        <v-icon x-small>fas fa-trash</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div v-else class="pa-4 caption grey--text">
              Opcional. Busca un artículo de bodega y registra la cantidad usada en esta OT.
            </div>
          </v-card>
        </div>

        <!-- Form Actions -->
        <div class="d-flex justify-end pt-8 gap-3">
          <v-btn text color="grey darken-1" class="font-weight-bold px-8" @click="$emit('close')">Cancelar</v-btn>
          <v-btn type="submit" color="primary" depressed class="font-weight-bold px-10 shadow-sm" :loading="loading">
            <v-icon small class="mr-2">fas fa-paper-plane</v-icon> Enviar Reporte
          </v-btn>
        </div>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { postProcessOrderComplete, createComsumptionBitacora } from '@/api/production'
import notifications from '@/utils/notifications'
import AddArticle from '@/components/stocks/AddArticle'

export default {
  name: 'ProcessComplete',
  components: { AddArticle },
  props: ['processes', 'machine'],
  data() {
    return {
      equipment: '',
      mts2: 0,
      qty: 0,
      loading: false,
      notes: '',
      otMaterials: {},
    }
  },
  methods: {
    async save() {
      this.loading = true
      const proccessesToComplete = this.processes.map(p => {
        if (this.machine.production_unit_m2) this.calculateQty(p)
        else this.calculateMts2(p)
        const real_mts2 = parseFloat(p.real_mts2) || 0
        const real_qty = parseFloat(p.real_qty) || 0
        return {
          id: parseInt(p.process),
          real_mts2: real_mts2 > 1000000 ? 0 : real_mts2,
          real_qty: real_qty > 1000000 ? 0 : real_qty,
          ask_completed: Boolean(p.ask_completed),
          start: p.start ? new Date(p.start).toISOString() : new Date().toISOString(),
          end: p.end ? new Date(p.end).toISOString() : new Date().toISOString(),
        }
      })

      if (this.machine.hour_control) {
        const invalidDates = proccessesToComplete.filter(p => (new Date(p.start) > new Date(p.end)))
        if (invalidDates.length > 0) {
          notifications.showError('La fecha de término es inválida')
          this.loading = false
          return
        }
      }

      let productionSaved = false
      try {
        await postProcessOrderComplete({
          processes: proccessesToComplete,
          equipment: this.equipment,
          notes: this.notes
        })
        productionSaved = true
        await this.saveConsumptions()
        notifications.showSuccess('Procesos registrados correctamente')
        this.$emit('refresh')
        this.$emit('close')
      } catch (e) {
        if (productionSaved) {
          notifications.showError('La producción se guardó, pero el consumo de materiales falló. Revísalo en la OT.')
          this.$emit('refresh')
          this.$emit('close')
        } else {
          notifications.showError('Error al registrar: ' + this.formatApiError(e))
        }
      } finally {
        this.loading = false
      }
    },
    formatApiError(e) {
      const data = e && e.response && e.response.data
      if (typeof data === 'string' && data.trim()) return data
      if (data && data.error) return data.error
      if (data && data.detail) return data.detail
      if (data && typeof data === 'object') {
        const parts = Object.keys(data).map((key) => {
          const val = data[key]
          if (Array.isArray(val)) return val.join(', ')
          return typeof val === 'string' ? val : ''
        }).filter(Boolean)
        if (parts.length) return parts.join(' ')
      }
      return (e && e.message) || String(e)
    },
    productArea(item) {
      const width = parseFloat(item && item.product_width)
      const height = parseFloat(item && item.product_height)
      if (width > 0 && height > 0) return width * height * 0.0001
      return 0
    },
    productIdOf(process) {
      if (!process) return null
      if (process.product && typeof process.product === 'object') return process.product.id
      return process.product
    },
    addMaterial(workOrder, article) {
      if (!article || !article.id) return
      const factor = parseFloat(article.unit_consume_factor) || 1
      const line = {
        key: Date.now() + '-' + article.id + '-' + Math.random(),
        article,
        qty: 1,
        reprocess: false,
        unitConsume: article.unit_consume_name || article.unit_name || '',
        factor,
      }
      const list = (this.otMaterials[workOrder] || []).concat([line])
      this.$set(this.otMaterials, workOrder, list)
    },
    removeMaterial(workOrder, index) {
      const list = (this.otMaterials[workOrder] || []).slice()
      list.splice(index, 1)
      this.$set(this.otMaterials, workOrder, list)
    },
    async saveConsumptions() {
      const jobs = []
      this.otGroups.forEach((group) => {
        (this.otMaterials[group.work_order] || []).forEach((line) => {
          const qty = parseFloat(line.qty)
          if (!line.article || !group.product_id || !qty || qty <= 0) return
          const avg = parseFloat(line.article.avg) || 0
          const factor = parseFloat(line.factor) || 1
          jobs.push(createComsumptionBitacora({
            article_id: line.article.id,
            product_id: group.product_id,
            articlePrice: avg,
            convertedPrice: avg * factor,
            qty,
            unit: line.article.unit_name,
            unitConsume: line.unitConsume,
            unitConsume_factor: factor,
            reprocess: !!line.reprocess,
          }))
        })
      })
      if (jobs.length) await Promise.all(jobs)
    },
    calculateQty(item) {
      const mts2 = parseFloat(item.real_mts2) || 0
      const area = this.productArea(item)
      if (area > 0) {
        item.real_qty = mts2 / area
      } else {
        const targetMts2 = parseFloat(item.product_mts2) || 0
        const targetQty = parseFloat(item.product_qty) || 0
        item.real_qty = targetMts2 > 0 ? mts2 * (targetQty / targetMts2) : 0
      }
      if (!isFinite(item.real_qty) || item.real_qty > 1000000) item.real_qty = 0
      this.suma()
    },
    calculateMts2(item) {
      const qty = parseFloat(item.real_qty) || 0
      const area = this.productArea(item)
      if (area > 0) {
        item.real_mts2 = qty * area
      } else {
        const targetMts2 = parseFloat(item.product_mts2) || 0
        const targetQty = parseFloat(item.product_qty) || 0
        item.real_mts2 = targetQty > 0 ? qty * (targetMts2 / targetQty) : 0
      }
      if (!isFinite(item.real_mts2) || item.real_mts2 > 1000000) item.real_mts2 = 0
      this.suma()
    },
    suma() {
      this.qty = 0
      this.mts2 = 0
      this.processes.forEach(proc => {
        this.qty += parseFloat(proc.real_qty || 0)
        this.mts2 += parseFloat(proc.real_mts2 || 0)
      })
    },
  },
  watch: { 
    processes: {
      handler() {
        this.suma()
        this.otGroups.forEach((group) => {
          if (!this.otMaterials[group.work_order]) this.$set(this.otMaterials, group.work_order, [])
        })
      },
      immediate: true
    }
  },
  computed: {
    hour_control() {
      return this.machine.hour_control
    },
    otGroups() {
      const groups = []
      const seen = {}
      ;(this.processes || []).forEach((process) => {
        const ot = process.work_order
        if (ot == null || seen[ot]) return
        seen[ot] = true
        groups.push({
          work_order: ot,
          product_id: this.productIdOf(process),
          product_name: process.product_name || (process.product && process.product.name) || '',
        })
      })
      return groups
    }
  }
}
</script>

<style scoped>
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; }
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-primary-light { border: 1px solid rgba(var(--v-primary-base), 0.1); }
.opacity-20 { background-color: rgba(255,255,255,0.2) !important; }
.opacity-70 { opacity: 0.7; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; }
.gap-1 { gap: 4px; }
.gap-3 { gap: 12px; }
.modern-input-mini >>> .v-input__slot { min-height: 32px !important; font-size: 0.75rem !important; }
.modern-input-mini >>> .v-label { font-size: 0.75rem !important; }
.modern-table >>> td { padding: 12px 16px !important; }
.qty-input { max-width: 140px; }
.qty-input >>> input {
  text-align: right;
  font-weight: 700;
}
.qty-input >>> input::-webkit-outer-spin-button,
.qty-input >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.qty-input >>> input[type=number] {
  -moz-appearance: textfield;
}
</style>
